/**
 * `/api/audit` — reading the forensic record.
 *
 * Read-only, and there is no write function here to add: the log is append-only
 * and nothing edits it. An audit log the application can change is not evidence.
 */
import type { AuditHistoryResponse, AuditListResponse } from '@forsys/shared';
import { apiFetch, query } from './client';

/** The filters, mirroring `forsys audit:tail` exactly. */
export interface AuditQuery {
  /** An event name, or a family with a trailing star: `resource.grant.*`. */
  event?: string;
  entityType?: string;
  entityId?: string;
  actorId?: number;
  /** Everything one operation did, across every log line and row. */
  correlationId?: string;
  /** ISO-8601. Only events at or after it. */
  since?: string;
  limit?: number;
  /** The cursor: the last id of the previous page. */
  beforeId?: string;
}

/**
 * The log, newest first.
 *
 * Paging is by `beforeId`, not by offset — the log grows at the end that is read
 * first, so an offset page would shift under the reader as new rows arrive.
 * Pass the response's `nextBeforeId` back to get the page below it; `null` means
 * the end.
 */
export function listAuditEvents(params: AuditQuery = {}): Promise<AuditListResponse> {
  return apiFetch<AuditListResponse>(`/audit${query({ ...params })}`);
}

/** Everything that ever happened to one entity, oldest first. */
export function getEntityHistory(
  entityType: string,
  entityId: string,
  limit?: number,
): Promise<AuditHistoryResponse> {
  return apiFetch<AuditHistoryResponse>(
    `/audit/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}${query({ limit })}`,
  );
}
